import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Widget from './Widget';
import { listNews } from '../../api/api';

/**
 * Strips HTML tags and decodes HTML entities from a string
 * @param {string} html - HTML string to clean
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  if (!html) return '';
  // Create a temporary DOM element to parse HTML
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Get text content (strips all tags)
  let text = doc.body.textContent || '';
  // Clean up extra whitespace
  text = text.replace(/\s+/g, ' ').trim();
  return text;
};

/**
 * Truncates text to a maximum length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum character length
 * @returns {string} Truncated text
 */
const truncateText = (text, maxLength = 200) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

const labelize = (value) => String(value || '').replace(/_/g, ' ');

const hasNumber = (value) => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));

const formatMetric = (value, { digits = 2, signed = false } = {}) => {
  if (!hasNumber(value)) return null;
  const numeric = Number(value);
  const fixed = numeric.toFixed(digits);
  const trimmed = fixed.replace(/\.00$/, '').replace(/(\.\d*[1-9])0+$/, '$1');
  return signed && numeric > 0 ? `+${trimmed}` : trimmed;
};

const sentimentTone = (direction) => {
  const normalized = String(direction || '').toLowerCase();
  if (normalized === 'positive') return 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30';
  if (normalized === 'negative') return 'bg-rose-500/15 text-rose-300 border-rose-400/30';
  if (normalized === 'neutral') return 'bg-sky-500/15 text-sky-300 border-sky-400/30';
  return 'bg-secondary/40 text-textSecondary border-border/40';
};

const compactList = (values, limit = 3) => {
  const items = Array.isArray(values) ? values.filter(Boolean) : [];
  if (!items.length) return null;
  const visible = items.slice(0, limit).map((item) => labelize(item));
  const remaining = items.length - visible.length;
  return remaining > 0 ? `${visible.join(', ')} +${remaining}` : visible.join(', ');
};

/**
 * NewsWidget - Displays financial news feed
 * 
 * Props:
 * - hideTitle: boolean - hide widget title
 * - limit: number - max news items to display
 * - autoRefreshMs: number - auto refresh interval (0 to disable)
 * - source: string - filter by news source
 */
const NewsWidget = ({ 
  hideTitle = false, 
  limit = 20,
  autoRefreshMs = 60000,
  source = null 
}) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchNews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page_size: limit };
      if (source) params.source = source;
      const data = await listNews(params);
      const newsArray = Array.isArray(data) ? data : (data?.results || []);
      setNews(newsArray.slice(0, limit));
    } catch (err) {
      console.error('Error fetching news:', err);
      setError('Failed to load news');
    } finally {
      setLoading(false);
    }
  }, [source, limit]);

  useEffect(() => {
    fetchNews();
    
    if (autoRefreshMs > 0) {
      const interval = setInterval(fetchNews, autoRefreshMs);
      return () => clearInterval(interval);
    }
  }, [fetchNews, autoRefreshMs]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Widget title="News" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {/* Header with refresh */}
        <div className="flex items-center justify-between px-2 py-1 border-b border-borderDefault">
          <span className="text-xs text-textSecondary">
            {news.length} articles
          </span>
          <button
            onClick={fetchNews}
            disabled={loading}
            className="text-xs text-primary hover:text-primary/80 disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {/* News list */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="p-4 text-center text-destructive text-sm">{error}</div>
          )}
          
          {!error && news.length === 0 && !loading && (
            <div className="p-4 text-center text-textSecondary text-sm">No news available</div>
          )}

          {!error && news.map((item, idx) => (
            (() => {
              const sentimentScore = formatMetric(item.sentiment_score, { digits: 2, signed: true });
              const impactAmount = formatMetric(item.impact_amount, { digits: 2, signed: true });
              const confidence = formatMetric(item.enrichment_confidence, { digits: 2 });
              const psychologyScore = formatMetric(item.psychological_impact_score, { digits: 2, signed: true });
              const symbols = compactList(item.impacted_symbols);
              const countries = compactList(item.impacted_countries);
              const industries = compactList(item.impacted_industries);
              const psychologicalText = stripHtml(item.psychological_impact_text || '');
              const aiSummaryParts = [];

              if (symbols) aiSummaryParts.push(`Symbols: ${symbols}`);
              if (!symbols && countries) aiSummaryParts.push(`Countries: ${countries}`);
              if (industries) aiSummaryParts.push(`Industries: ${industries}`);
              if (!industries && symbols && countries) aiSummaryParts.push(`Countries: ${countries}`);
              if (psychologicalText) aiSummaryParts.push(`AI view: ${truncateText(psychologicalText, 90)}`);

              const aiSummary = aiSummaryParts.slice(0, 2).join(' • ');

              return (
                <div
                  key={item.id || idx}
                  className="p-2 sm:p-3 border-b border-borderDefault hover:bg-secondary/10 transition-colors cursor-pointer"
                  onClick={() => item.url && window.open(item.url, '_blank')}
                >
                  <div className="flex items-start justify-between gap-1 sm:gap-2">
                    <h4 className="flex-1 text-sm font-semibold leading-5 text-textPrimary line-clamp-2 sm:text-[15px]">
                      {stripHtml(item.headline || item.title)}
                    </h4>
                    <span className="text-[10px] sm:text-xs text-textSecondary whitespace-nowrap flex-shrink-0">
                      {formatTime(item.time || item.published_at)}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px] sm:text-xs">
                    {item.source ? (
                      <span className="text-textSecondary">{item.source}</span>
                    ) : null}
                    {item.sentiment_direction ? (
                      <span className={`inline-flex rounded-full border px-1.5 py-0.5 ${sentimentTone(item.sentiment_direction)}`}>
                        {labelize(item.sentiment_direction)}{sentimentScore ? ` ${sentimentScore}` : ''}
                      </span>
                    ) : null}
                    {impactAmount ? (
                      <span className="inline-flex rounded-full border border-primary/20 bg-primary/10 px-1.5 py-0.5 text-primary">
                        Impact {impactAmount}
                      </span>
                    ) : null}
                    {confidence ? (
                      <span className="inline-flex rounded-full border border-border/40 px-1.5 py-0.5 text-textSecondary">
                        AI {confidence}
                      </span>
                    ) : null}
                    {psychologyScore ? (
                      <span className="inline-flex rounded-full border border-amber-400/30 bg-amber-500/10 px-1.5 py-0.5 text-amber-200">
                        Psych {psychologyScore}
                      </span>
                    ) : null}
                  </div>

                  {aiSummary ? (
                    <p className="mt-1 text-[10px] sm:text-xs text-textSecondary line-clamp-1">
                      {aiSummary}
                    </p>
                  ) : null}

                  {item.content ? (
                    <p className="mt-1 text-[10px] sm:text-xs text-textSecondary line-clamp-1">
                      {truncateText(stripHtml(item.content), 120)}
                    </p>
                  ) : null}
                </div>
              );
            })()
          ))}
        </div>
      </div>
    </Widget>
  );
};

NewsWidget.propTypes = {
  hideTitle: PropTypes.bool,
  limit: PropTypes.number,
  autoRefreshMs: PropTypes.number,
  source: PropTypes.string,
};

export default NewsWidget;
